import type { GamificationEventType } from '@crowdfpl/domain/gamification';
import { create } from 'zustand';

export type ProgressTrophiesTab = 'progress' | 'trophies';
export type ProgressTrophiesReadMode = 'own' | 'public';

type ProgressTrophiesModalState = {
  isOpen: boolean;
  defaultTab: ProgressTrophiesTab;
  targetUserId: string | null;
  targetUsername: string | null;
  readMode: ProgressTrophiesReadMode;
};

type ProgressTrophiesModalActions = {
  openOwnProgress: () => void;
  openOwnTrophies: () => void;
  openPublicProfile: (userId: string, tab: ProgressTrophiesTab, username?: string | null) => void;
  openForEvent: (eventType: GamificationEventType) => void;
  close: () => void;
};

export type ProgressTrophiesModalStore = ProgressTrophiesModalState & ProgressTrophiesModalActions;

export const progressTrophiesModalInitialState: ProgressTrophiesModalState = {
  isOpen: false,
  defaultTab: 'progress',
  targetUserId: null,
  targetUsername: null,
  readMode: 'own',
};

function ownState(tab: ProgressTrophiesTab): ProgressTrophiesModalState {
  return {
    isOpen: true,
    defaultTab: tab,
    targetUserId: null,
    targetUsername: null,
    readMode: 'own',
  };
}

function tabByEvent(eventType: GamificationEventType): ProgressTrophiesTab {
  if (eventType === 'progress_tier_unlocked') {
    return 'progress';
  }
  return 'trophies';
}

export const useProgressTrophiesModalStore = create<ProgressTrophiesModalStore>(set => ({
  ...progressTrophiesModalInitialState,
  openOwnProgress: () => set(ownState('progress')),
  openOwnTrophies: () => set(ownState('trophies')),
  openPublicProfile: (userId, tab, username = null) =>
    set({
      isOpen: true,
      defaultTab: tab,
      targetUserId: userId,
      targetUsername: username?.trim() ? username.trim().replace(/^@+/, '') : null,
      readMode: 'public',
    }),
  openForEvent: eventType => set(ownState(tabByEvent(eventType))),
  close: () =>
    set({
      ...progressTrophiesModalInitialState,
      isOpen: false,
    }),
}));
